import { Box, Button, Icon, Menu, Paragraph } from '@zillow/constellation-10';
import { IconListNumberedFilled } from '@zillow/constellation-icons';
import { FULLSTORY_EXCLUDE_CLASS_NAME } from '@zg-rentals/satellite-components';
import type { SatelliteWebLandlordLeadManagementTableRowResponseDto } from '@zg-rentals/ts-satellite-client';
import getRenterDisplayName from '../../../../utils/getRenterDisplayName';
import { leadJumpMenuGroupStyles, leadJumpTriggerContentStyles, leadJumpTriggerStyles } from './styles';

type LeadJumpMenuProps = {
  leads: Array<SatelliteWebLandlordLeadManagementTableRowResponseDto>;
  currentLeadIndex: number;
  onNavigateToLead: (conversationId: string) => void;
};

/**
 * Jump straight to any lead in the drawer's filtered list — prev/next steps one lead at a time,
 * which doesn't scale to distant jumps (e.g. lead 2 to lead 15).
 */
export default function LeadJumpMenu({ leads, currentLeadIndex, onNavigateToLead }: LeadJumpMenuProps) {
  if (leads.length === 0) {
    return null;
  }

  const counterText = currentLeadIndex === -1 ? `${leads.length} leads` : `${currentLeadIndex + 1} of ${leads.length}`;

  return (
    <Menu
      content={
        <Menu.Group aria-label="Jump to lead" css={leadJumpMenuGroupStyles}>
          {leads.map((lead, index) => (
            <Menu.Item
              key={lead.conversationId}
              disabled={index === currentLeadIndex}
              onClick={() => onNavigateToLead(lead.conversationId)}
            >
              <Menu.ItemLabel className={FULLSTORY_EXCLUDE_CLASS_NAME}>
                {getRenterDisplayName(lead.renterInfo?.renterName, lead.latestContact?.isPhoneLead)}
              </Menu.ItemLabel>
            </Menu.Item>
          ))}
        </Menu.Group>
      }
      placement="bottom-start"
      trigger={
        <Button emphasis="bare" title="Jump to lead" css={leadJumpTriggerStyles}>
          <Box css={leadJumpTriggerContentStyles}>
            <Icon render={<IconListNumberedFilled />} css={{ color: 'text.subtle' }} />
            <Paragraph textStyle="body-sm" css={{ color: 'text.subtle', whiteSpace: 'nowrap' }}>
              {counterText}
            </Paragraph>
          </Box>
        </Button>
      }
      css={{ fontFamily: 'sansSerif' }}
    />
  );
}
